// src/ui/VoiceRecorder.jsx — the composer becomes the recorder.
//
// No press-and-hold: this is a browser, where a mouse-down held across a 40-second memo
// is genuinely worse than a click. Tap to arm, tap to stop, then a real preview you can
// scrub before it goes anywhere.
//
// Three states in one strip, so nothing jumps: ARMING → RECORDING → PREVIEW.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Send, Play, Pause, Mic, Trash } from './icons';
import { startRecording, timeLabel, MAX_SECONDS, WAVE_MIN, WAVE_MAX } from '../services/voice';

const LIVE_BARS = 46; // how many live bars are on screen at once

export default function VoiceRecorder({ onCancel, onSend }) {
  const [phase, setPhase] = useState('arming');   // arming | recording | preview
  const [seconds, setSeconds] = useState(0);
  const [live, setLive] = useState([]);           // rolling live levels
  const [result, setResult] = useState(null);     // {bytes, blob, durationSec, waveform}
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const recRef = useRef(null);
  const audioRef = useRef(null);
  const urlRef = useRef(null);
  const rafRef = useRef(0);

  // same reason as VoiceBubble: `timeupdate` is ~4Hz, so the preview playhead stepped
  // instead of gliding. Read currentTime per frame while playing.
  useEffect(() => {
    if (!playing) { cancelAnimationFrame(rafRef.current); return undefined; }
    const tick = () => {
      const el = audioRef.current;
      if (el) setPos(el.currentTime);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing]);

  // ── arm the mic immediately; the user already asked for this by clicking ──
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const rec = await startRecording({
          onLevel: (lvl) => {
            if (dead) return;
            setLive((prev) => {
              const next = prev.length >= LIVE_BARS ? prev.slice(1) : prev.slice();
              next.push(lvl);
              return next;
            });
          },
          onTick: (s) => { if (!dead) setSeconds(s); },
        });
        if (dead) { rec.cancel(); return; }
        recRef.current = rec;
        setPhase('recording');
      } catch (e) {
        if (!dead) { setError(e?.message || 'Could not start recording.'); }
      }
    })();
    return () => {
      dead = true;
      try { recRef.current?.cancel(); } catch {}
      if (urlRef.current) { try { URL.revokeObjectURL(urlRef.current); } catch {} }
    };
  }, []);

  const stop = useCallback(async () => {
    const rec = recRef.current;
    if (!rec) return;
    recRef.current = null;
    try {
      const out = await rec.stop();
      urlRef.current = URL.createObjectURL(out.blob);
      setResult(out);
      setPos(0);
      setPhase('preview');
    } catch (e) {
      setError(e?.message || 'Recording failed.');
    }
  }, []);

  // hard stop at the cap rather than letting a forgotten tab record for ever
  useEffect(() => {
    if (phase === 'recording' && seconds >= MAX_SECONDS) stop();
  }, [phase, seconds, stop]);

  // Esc discards, Enter sends the preview
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
      if (e.key === 'Enter' && phase === 'preview') { e.preventDefault(); doSend(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) { el.play(); setPlaying(true); } else { el.pause(); setPlaying(false); }
  };

  const doSend = async () => {
    if (!result || sending) return;
    setSending(true);
    try {
      await onSend(result);
    } catch (e) {
      setError(e?.message || 'Could not send.');
      setSending(false);
    }
  };

  if (error) {
    return (
      <div className="composer vr">
        <div className="vr-error">{error}</div>
        <button className="icon-btn" onClick={onCancel} title="Close"><X /></button>
      </div>
    );
  }

  // ── PREVIEW ──
  if (phase === 'preview' && result) {
    const total = result.durationSec || 1;
    const played = Math.min(1, pos / total);
    return (
      <div className="composer vr">
        <button className="icon-btn vr-trash" title="Discard" onClick={onCancel}><Trash /></button>

        <div className="vr-strip">
          <button className="vr-play" onClick={togglePlay} title={playing ? 'Pause' : 'Play'}>
            {playing ? <Pause width={17} height={17} /> : <Play width={17} height={17} />}
          </button>

          <div
            className="vr-wave"
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              const ratio = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
              if (audioRef.current) { audioRef.current.currentTime = ratio * total; setPos(ratio * total); }
            }}
          >
            {result.waveform.map((h, i) => {
              const on = (i + 0.5) / result.waveform.length <= played;
              return (
                <span
                  key={i}
                  className={`vr-bar ${on ? 'on' : ''}`}
                  style={{ height: `${(h / WAVE_MAX) * 100}%` }}
                />
              );
            })}
            <span className="vr-thumb" style={{ left: `${played * 100}%` }} />
          </div>

          <span className="vr-time">{timeLabel(playing || pos ? pos : total)}</span>
        </div>

        <button className="send-btn" onClick={doSend} disabled={sending} title="Send">
          <Send />
        </button>

        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio
          ref={audioRef}
          src={urlRef.current || undefined}
          onTimeUpdate={(e) => { if (!playing) setPos(e.currentTarget.currentTime); }}
          onEnded={() => { setPlaying(false); setPos(0); }}
        />
      </div>
    );
  }

  // ── ARMING / RECORDING ──
  const arming = phase === 'arming';
  return (
    <div className="composer vr">
      <button className="icon-btn vr-trash" title="Cancel" onClick={onCancel}><Trash /></button>

      <div className="vr-strip recording">
        <span className={`vr-dot ${arming ? 'arming' : ''}`} />
        <span className="vr-time mono">{timeLabel(seconds)}</span>

        <div className="vr-live">
          {arming
            ? <span className="vr-arming">Starting the microphone…</span>
            : live.map((lvl, i) => {
              const h = WAVE_MIN + Math.max(0, Math.min(1, lvl)) * (WAVE_MAX - WAVE_MIN);
              return (
                <span
                  key={i}
                  className="vr-bar live"
                  style={{ height: `${(h / WAVE_MAX) * 100}%`, opacity: 0.35 + (i / live.length) * 0.65 }}
                />
              );
            })}
        </div>

        <span className="vr-hint">{MAX_SECONDS - seconds <= 30 ? `${MAX_SECONDS - seconds}s left` : 'Recording'}</span>
      </div>

      <button className="send-btn vr-stop" onClick={stop} disabled={arming} title="Stop">
        <Mic />
      </button>
    </div>
  );
}
